import { CircularProgress } from '../CircularProgress';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { KPICell, SectionFrame, performanceColor, type PeriodContentProps } from './shared';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';

const PALETTE = {
  onTime: '#0f766e',
  early:  '#2563eb',
  late:   '#dc2626',
  pending: '#cbd5e1',
};

export function CurrentWeek({ data, formatCurrency }: PeriodContentProps) {
  const cw = data.current_week;
  const qualityLabel = useQualityMeasureLabel();
  const deliveriesByDay = cw.deliveries_by_day.map((d) => ({
    day: d.day,
    onTime: d.on_time,
    early: d.early,
    late: d.late,
    pending: d.pending,
  }));

  const otdSplit = [
    { name: 'On time', value: cw.delivery.on_time, color: PALETTE.onTime },
    { name: 'Early',   value: cw.delivery.early,   color: PALETTE.early },
    { name: 'Late',    value: cw.delivery.late,    color: PALETTE.late },
  ];
  const otdEmpty = otdSplit.every(d => d.value === 0);
  const otdData = otdEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9' }]
    : otdSplit;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Quality" accent="red" subtitle="Sun → today">
          <div className="grid grid-cols-2 gap-3">
            <KPICell
              label={qualityLabel}
              value={<span className="text-2xl font-semibold tabular-nums">{cw.quality.ppm.toLocaleString()}</span>}
              foot={`${cw.quality.reject_qty.toLocaleString()} rej`}
            />
            <KPICell
              label="NCRs"
              value={<span className="text-2xl font-semibold tabular-nums">{cw.quality.ncrs.toLocaleString()}</span>}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green" subtitle={cw.frozen ? 'Frozen at Thu 17:00' : 'Sun → today'}>
          <div className="grid grid-cols-3 gap-3 items-start">
            <KPICell
              label="OTIF"
              value={<CircularProgress percentage={cw.delivery.otif_pct} size={68} strokeWidth={6} color={performanceColor(cw.delivery.otif_pct)} />}
              foot={`${cw.delivery.otif_met}/${cw.delivery.total_due} schedules`}
            />
            <KPICell
              label="OTD"
              value={<CircularProgress percentage={cw.delivery.otd_pct} size={68} strokeWidth={6} color={performanceColor(cw.delivery.otd_pct)} />}
              foot={`${cw.delivery.otd_met}/${cw.delivery.total_due} within firm`}
            />
            <KPICell
              label="Appeals"
              value={<span className="text-2xl font-semibold tabular-nums">{cw.delivery.appeals}</span>}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle="schedules due this week">
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label="Open Schedule Lines"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.open_value)}</span>}
              foot={`${cw.cost.open_lines} of ${cw.cost.total_lines} lines`}
            />
            <KPICell
              label="Received"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.received_value)}</span>}
              foot={`of ${formatCurrency(cw.cost.total_value)} committed`}
            />
            <KPICell
              label="Arrears"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.arrears_value)}</span>}
              foot={`${cw.cost.arrears_lines} past-due lines`}
            />
          </div>
        </SectionFrame>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Deliveries by Day</CardTitle>
            <p className="text-[11px] text-muted-foreground">Sunday through Saturday • this week</p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliveriesByDay} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="onTime"  stackId="a" fill={PALETTE.onTime} name="On time" />
                  <Bar dataKey="early"   stackId="a" fill={PALETTE.early}  name="Early" />
                  <Bar dataKey="late"    stackId="a" fill={PALETTE.late}   name="Late" />
                  <Bar dataKey="pending" stackId="a" fill={PALETTE.pending} name="Pending" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">OTD Mix</CardTitle>
            <p className="text-[11px] text-muted-foreground">Week to date • {cw.delivery.total_with_status.toLocaleString()} attributed deliveries</p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={otdData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={56}
                    outerRadius={84}
                    paddingAngle={otdEmpty ? 0 : 2}
                    stroke="none"
                  >
                    {otdData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
